import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import FormModal from 'components/FormModal';
import BottomSheet from 'components/DesignSystem/BottomSheet';
import Switch from 'components/DesignSystem/Switch';
import Button from 'components/DesignSystem/Button';
import Tag from 'components/DesignSystem/Tag';
import Icon from 'components/DesignSystem/Icon';
import useIsMobile from 'utils/hooks/useIsMobile';
import { FluxAboutDocument } from 'types/Flux/About/fluxAboutDocument';
import {
  useCreateFluxAboutDocumentMutation,
  useUpdateFluxAboutDocumentMutation,
} from 'services/api/fluxAbout';

import './index.scss';

export interface AboutModalProps {
  open: boolean;
  onClose: () => void;
  document: FluxAboutDocument | null;
}

export default function AboutModal({
  open, onClose, document,
}: AboutModalProps) {
  const isEditing = document !== null;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [autor, setAutor] = useState('');
  const [ordem, setOrdem] = useState(0);
  const [ativo, setAtivo] = useState(true);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const [
    createDoc, { isLoading: isCreating },
  ] = useCreateFluxAboutDocumentMutation();
  const [
    updateDoc, { isLoading: isUpdating },
  ] = useUpdateFluxAboutDocumentMutation();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (document) {
      setTitulo(document.titulo);
      setDescricao(document.descricao);
      setAutor(document.autor);
      setOrdem(document.ordem);
      setAtivo(document.ativo);
    } else {
      setTitulo('');
      setDescricao('');
      setAutor('');
      setOrdem(0);
      setAtivo(true);
    }
    setPdfFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [document]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      setPdfFile(file);
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (isEditing) {
      await updateDoc({
        id: document.id,
        titulo,
        descricao,
        autor,
        ordem,
        ativo,
        ...(pdfFile ? { pdf: pdfFile } : {}),
      });
    } else {
      if (!pdfFile) return;
      await createDoc({
        titulo,
        descricao,
        autor,
        ordem,
        pdf: pdfFile,
      });
    }
    onClose();
  }, [
    titulo, descricao, autor, ordem, ativo, pdfFile,
    isEditing, document, updateDoc, createDoc, onClose,
  ]);

  const isLoading = isCreating || isUpdating;
  const canSubmit = isEditing || (titulo && pdfFile);

  const modalTitle = isEditing ? 'Editar Documento' : 'Novo Documento';

  const body = (
    <div>
      {isEditing && (
      <div className="about-modal-status-bar mb-4">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted about-modal-id-label">
            {`ID: ${document.id}`}
          </span>
          <Tag
            type={ativo ? 'completed' : 'cancelled'}
            icon={ativo ? 'checkmark' : 'close'}
          >
            {ativo ? 'Ativo' : 'Inativo'}
          </Tag>
        </div>
      </div>
      )}

      <p className="about-modal-section-title">
        <Icon iconType="edit" size="small" />
        Informações
      </p>

      <Row>
        <Col md={8}>
          <Form.Group className="mb-3" controlId="aboutTitulo">
            <Form.Label>Título</Form.Label>
            <Form.Control
              type="text"
              value={titulo}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement>,
              ) => setTitulo(e.target.value)}
            />
            <Form.Text className="text-muted">
              Nome que aparecerá na aba
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3" controlId="aboutOrdem">
            <Form.Label>Ordem</Form.Label>
            <Form.Control
              type="number"
              value={ordem}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement>,
              ) => setOrdem(Number(e.target.value))}
            />
            <Form.Text className="text-muted">
              Posição na lista
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3" controlId="aboutAutor">
        <Form.Label>Autor</Form.Label>
        <Form.Control
          type="text"
          value={autor}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement>,
          ) => setAutor(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="aboutDescricao">
        <Form.Label>Descrição</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={descricao}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement>,
          ) => setDescricao(e.target.value)}
        />
      </Form.Group>

      <p className="about-modal-section-title mt-2">
        <Icon iconType="upload" size="small" />
        Arquivo
      </p>

      <Form.Group className="mb-3">
        <Form.Label className="about-modal-file-label">
          {isEditing
            ? 'PDF (opcional — substituir)'
            : 'PDF (obrigatório)'}
        </Form.Label>
        <Form.Control
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileChange}
        />
        {isEditing && (
        <Form.Text className="text-muted d-flex align-items-center gap-1 mt-1">
          <Icon iconType="pdf" size="small" />
          {`Arquivo atual: ${document.object_name}`}
        </Form.Text>
        )}
      </Form.Group>

      {isEditing && (
      <>
        <p className="about-modal-section-title mt-2">
          <Icon iconType="toggle" size="small" />
          Configurações
        </p>

        <Form.Group className="d-flex align-items-center gap-3 mb-3">
          <Form.Label className="mb-0">Ativo</Form.Label>
          <Switch
            checked={ativo}
            onChange={(
              e: React.ChangeEvent<HTMLInputElement>,
            ) => setAtivo(e.target.checked)}
            className={isMobile ? 'ds-switch-pill' : ''}
          />
        </Form.Group>

        <Row className="about-modal-readonly-section">
          <Col md={6}>
            <Form.Group className="mb-3" controlId="aboutBucket">
              <Form.Label>Nome no Bucket</Form.Label>
              <Form.Control
                type="text"
                value={document.object_name}
                disabled
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3" controlId="aboutCreatedAt">
              <Form.Label>Criado em</Form.Label>
              <Form.Control
                type="text"
                value={document.created_at}
                disabled
                readOnly
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3" controlId="aboutUpdatedAt">
              <Form.Label>Atualizado em</Form.Label>
              <Form.Control
                type="text"
                value={document.updated_at}
                disabled
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>
      </>
      )}
    </div>
  );

  const footer = (
    <div className="d-flex justify-content-end">
      <Button
        icon={isEditing ? 'checkmark' : 'add'}
        onClick={handleSubmit}
        disabled={isLoading || !canSubmit}
      >
        {isEditing ? 'Salvar' : 'Criar'}
      </Button>
    </div>
  );

  // No mobile o formulário sobe de baixo num BottomSheet (mesmo padrão de
  // Usuários/Empresas/Modelos); no desktop segue no FormModal centralizado.
  if (isMobile) {
    return (
      <BottomSheet open={open} dismiss={onClose} title={modalTitle}>
        {body}
        {footer}
      </BottomSheet>
    );
  }

  return (
    <FormModal show={open} title={modalTitle} onHide={onClose} lastAsFooter>
      {body}
      {footer}
    </FormModal>
  );
}
